import { Controller } from "@hotwired/stimulus"

const GAP = 32
const FRICTION = 0.92
const MIN_VELOCITY = 0.4

export default class extends Controller {
  static targets = ["track"]

  connect() {
    this.dragging = false
    this.moved = false
    this.velocity = 0
    this.momentumFrame = null
    this.onMove = this.move.bind(this)
    this.onStop = this.stop.bind(this)
  }

  disconnect() {
    cancelAnimationFrame(this.momentumFrame)
    this.removeDragListeners()
  }

  next() {
    const card = this.trackTarget.querySelector("[data-carousel-item]")
    if (card) {
      this.trackTarget.scrollBy({ left: card.offsetWidth + GAP, behavior: "smooth" })
    }
  }

  prev() {
    const card = this.trackTarget.querySelector("[data-carousel-item]")
    if (card) {
      this.trackTarget.scrollBy({ left: -(card.offsetWidth + GAP), behavior: "smooth" })
    }
  }

  // Mouse drag-to-scroll (desktop). Touch devices use native scrolling.
  start(event) {
    if (event.pointerType !== "mouse") return

    cancelAnimationFrame(this.momentumFrame)
    this.dragging = true
    this.moved = false
    this.velocity = 0
    this.lastX = event.clientX
    this.startX = event.clientX
    this.startScroll = this.trackTarget.scrollLeft

    // Disable snap + smooth so the drag tracks the cursor 1:1.
    this.trackTarget.style.scrollSnapType = "none"
    this.trackTarget.classList.remove("scroll-smooth")

    // Track the drag on window (NOT setPointerCapture): capturing the pointer
    // retargets the subsequent `click` to the track, which swallows clicks on
    // interactive cards inside the carousel. Window listeners keep the drag
    // alive even when the cursor leaves the track, without breaking clicks.
    window.addEventListener("pointermove", this.onMove)
    window.addEventListener("pointerup", this.onStop)
    window.addEventListener("pointercancel", this.onStop)
  }

  move(event) {
    if (!this.dragging) return

    const delta = event.clientX - this.lastX
    this.lastX = event.clientX
    this.velocity = delta
    if (Math.abs(event.clientX - this.startX) > 3) this.moved = true

    this.trackTarget.scrollLeft -= delta
  }

  // Bound to pointerup AND pointercancel so an interrupted pointer (OS gesture,
  // window blur) can't leave dragging stuck true with snap disabled.
  stop() {
    if (!this.dragging) return

    this.dragging = false
    this.removeDragListeners()
    this.applyMomentum()
  }

  removeDragListeners() {
    window.removeEventListener("pointermove", this.onMove)
    window.removeEventListener("pointerup", this.onStop)
    window.removeEventListener("pointercancel", this.onStop)
  }

  applyMomentum() {
    if (Math.abs(this.velocity) < MIN_VELOCITY) {
      this.settle()
      return
    }

    this.trackTarget.scrollLeft -= this.velocity
    this.velocity *= FRICTION
    this.momentumFrame = requestAnimationFrame(() => this.applyMomentum())
  }

  // Restore snap (snaps to nearest card) and smooth scrolling.
  settle() {
    this.trackTarget.style.scrollSnapType = ""
    this.trackTarget.classList.add("scroll-smooth")
  }

  // Prevent a drag from triggering clicks/links on the cards.
  guardClick(event) {
    if (this.moved) {
      event.preventDefault()
      event.stopPropagation()
      this.moved = false
    }
  }
}
